// frontend/src/hooks/useSiteCategories.ts — the site-categories read surface
// (list), mirroring useSitePriorities. Project-scoped server state (FRONTEND-
// PATTERNS §State): the key is `['site-categories', projectId]` so a mutation's
// invalidate refreshes the list. The list row carries every field (no detail
// endpoint — the edit drawer pre-fills from the row), so there is no detail hook.
// The read is member-level (a vocabulary is named by every member; defining it
// stays `reference_data:manage`); the project's default category rides its row
// (`is_default` — the site wizard's pre-fill).
//
// 401 is the centralized apiClient's job (ADR-003) — errors bubble so the page's
// 5-state contract renders them.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { SiteCategoryListItem, SiteCategoriesListResponse } from '@ems-app/schemas/site-categories/responses';
import { apiClient } from '../lib/apiClient.js';
import type { SmallSource } from './useOptions.js';

export const siteCategoriesKeys = {
  /** List key — also the invalidation PREFIX. */
  all: (projectId: string) => ['site-categories', projectId] as const,
};

/** The list read — the page's, and a form's Load the latest (a category has no detail read: the list row is its read). */
export async function fetchSiteCategories(projectId: string): Promise<SiteCategoriesListResponse> {
  const r = await apiClient.get<SiteCategoriesListResponse>(`/projects/${projectId}/site-categories`);
  return r.data;
}

// Module-level selector (referentially stable, §State select doctrine).
const selectItems = (r: SiteCategoriesListResponse): SiteCategoryListItem[] => r.items;

export function useSiteCategories(projectId: string): UseQueryResult<SiteCategoryListItem[], Error> {
  return useQuery({
    queryKey: siteCategoriesKeys.all(projectId),
    queryFn: () => fetchSiteCategories(projectId),
    enabled: projectId.length > 0,
    select: selectItems,
  });
}

/** The categories as a picker's source (ADR-080 §3 — the small kind, on the list's own key): the WHOLE list on the
 *  press in its curated order; a disabled category is an inactive row. */
export function siteCategoriesSource(projectId: string): SmallSource<SiteCategoriesListResponse> {
  return {
    kind: 'small',
    queryKey: siteCategoriesKeys.all(projectId),
    fetch: () => fetchSiteCategories(projectId),
    rows: (r) => r.items.map((c) => ({ id: String(c.id), label: c.name, pickable: true, inactive: c.is_disabled, group: null })),
  };
}
